import * as React from 'react';
import {useState} from 'react';

import memoize from 'memoize-one';

import {RunHistoryRow} from '../types/run';
import {
  runFileSource,
  labelComponent,
  makeCaptions,
  useStepWithRollback,
} from '../util/media';
import {useLoadFile, useDownloadFile} from '../util/requests';
import {runLink} from '../util/runhelpers';
import {MediaCardProps} from './MediaCard';
import MessageMediaNotFound from './MessageMediaNotFound';
import LegacyWBIcon from './elements/LegacyWBIcon';
import makeComp from '../util/profiler';
import VideoViewer from './Panel2/VideoViewer';

type VideoCardProps = MediaCardProps;

// a single image card
const VideoCard = makeComp(
  (props: VideoCardProps) => {
    const {run, mediaKey, mediaIndex, runSignature, globalStep} = props;
    const downloadFile = useDownloadFile();

    const {currentStep, stepData, failedToLoad, rollback} = useStepWithRollback(
      props
    );

    const [refreshTimestamp, setRefreshTimestamp] = useState<number>(
      Date.now()
    );

    const media = getMedia(stepData, mediaKey, mediaIndex);

    // File path is the truncated path for using as an argument to the
    // GQL API
    let videoFile: string;
    // Src is the full path
    let videoSrc: string;
    let mediaNotFound: boolean;

    if (media) {
      videoFile = media.path;
      videoSrc = runFileSource(runSignature, media.path);
      mediaNotFound = false;
    } else {
      videoFile = '';
      videoSrc = '';
      mediaNotFound = true;
    }

    const titleLink = runLink(runSignature, run.displayName, {
      className: 'hide-in-run-page',
      target: '_blank',
      rel: 'noopener noreferrer',
    });

    const [domLoadFailed, setDomLoadFailed] = useState<boolean>();

    // Use the file loader, hook to poll with retries and fallback
    // on partial data
    //
    // ! Warning: The callbacks passed to useLoadFile must stay
    // equal by reference or it will retrigger a file upload.
    useLoadFile(runSignature, videoFile, {
      onSuccess: () => {
        setDomLoadFailed(false);
        setRefreshTimestamp(Date.now());
      },
      fallback: rollback,
      skip: !domLoadFailed || !media || failedToLoad,
    });

    const mediaFailedToLoad =
      failedToLoad || mediaNotFound || currentStep == null;

    // get captions for all images in the group
    const captions = stepData
      ? makeCaptions(stepData[mediaKey], mediaIndex)
      : [];

    return (
      <VideoViewer
        videoSrc={videoSrc}
        width={props.width}
        height={props.height}
        headerElement={labelComponent(props, currentStep, titleLink)}
        footerElement={[
          <>
            {captions.length > 0 && (
              <div className="image-card-caption">{captions}</div>
            )}
            {videoFile && (
              <LegacyWBIcon
                className="content-card__download"
                onClick={() => {
                  downloadFile(runSignature, videoFile);
                }}
                name="download"
              />
            )}
          </>,
        ]}
        failedLoadElement={[
          <MessageMediaNotFound
            mediaIndex={mediaIndex}
            mediaKey={mediaKey}
            stepIndex={globalStep}
            mediaType="videos"
          />,
        ]}
        refreshTimestamp={refreshTimestamp}
        mediaFailedToLoad={mediaFailedToLoad}
        single={props.single}
        setDomLoadFailed={setDomLoadFailed}
      />
    );
  },
  {id: 'VideoCard'}
);

export default VideoCard;

// Get the Media object logged for the given key and step.
const getMedia = memoize(
  (stepData: RunHistoryRow | undefined, mediaKey: string, index: number) => {
    if (!stepData || !stepData[mediaKey]) {
      return null;
    }
    const stepMedia = stepData[mediaKey];
    if (stepMedia._type === 'videos') {
      if (stepMedia.videos) {
        return stepMedia.videos[index];
      } else {
        return null;
      }
    } else if (stepMedia._type === 'video-file') {
      return stepMedia;
    } else {
      return null;
    }
  }
);
